import { useState } from "react";
import { BellRing, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { createAlert } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function effectiveCutoff(stream) {
  const latest = (stream.draws || []).find((r) => r.cutoff != null);
  return latest ? latest.cutoff : stream.cutoff;
}

export function DrawAlertForm({ stream, assessmentId, alertEmail, onSubscribed }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const cutoff = effectiveCutoff(stream);
  if (stream.scoring_model === "eligibility" || stream.score == null || cutoff == null || stream.score >= cutoff) return null;

  if (alertEmail) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-800" data-testid="draw-alert-done">
        <div className="flex items-start gap-2">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
          <span>Alert set. We'll email <strong>{alertEmail}</strong> once the {stream.name} cutoff drops to <strong>{stream.score}</strong> or below. Each email includes an unsubscribe link.</span>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email)) { toast.error("Please enter a valid email address"); return; }
    setBusy(true);
    try {
      const res = await createAlert({ email: email.trim(), streamId: stream.id, score: stream.score, assessmentId });
      onSubscribed(email.trim().toLowerCase());
      toast.success(res.already_subscribed ? "Alert updated with your new score" : "Draw alert set — check your inbox for a confirmation");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not set the alert. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="draw-alert-form">
      <div className="flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#C8102E] text-white"><BellRing className="h-5 w-5" /></span>
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-900">Get a draw alert</h3>
          <p className="text-xs text-slate-500">One email when this cutoff drops to {stream.score} or below (currently {cutoff}).</p>
        </div>
      </div>
      <form onSubmit={submit} className="mt-4 flex gap-2">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="draw-alert-email-input"
          className="min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-[#C8102E] focus:outline-none focus:ring-2 focus:ring-[#C8102E]/20" />
        <button type="submit" disabled={busy} data-testid="draw-alert-submit-btn"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-[#C8102E] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#A60C24] disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />} Alert me
        </button>
      </form>
      <p className="mt-2 text-xs text-slate-400">Your email is used only for this alert. Unsubscribe any time from the email.</p>
    </div>
  );
}
